import React, { useState } from 'react';
import { Heart, Info, Clock, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const Adopt: React.FC = () => {
  const [activeTab, setActiveTab] = useState('available');

  return (
    <div className="min-h-screen bg-cream-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-warm-coral to-sky-blue py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl text-white mb-4"
          >
            Find Your Perfect Match
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/90 text-xl max-w-3xl mx-auto"
          >
            Every pet in our care is spayed/neutered, microchipped, vaccinated, and ready to bring joy to your home.
          </motion.p>
        </div>
      </section>

      {/* Tabs */}
      <div className="sticky top-0 bg-white shadow-md z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('available')}
              className={`py-4 px-2 border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'available'
                  ? 'border-warm-coral text-warm-coral font-semibold'
                  : 'border-transparent text-warm-gray hover:text-deep-forest'
              }`}
            >
              Available Pets
            </button>
            <button
              onClick={() => setActiveTab('process')}
              className={`py-4 px-2 border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'process'
                  ? 'border-warm-coral text-warm-coral font-semibold'
                  : 'border-transparent text-warm-gray hover:text-deep-forest'
              }`}
            >
              Adoption Process
            </button>
            <button
              onClick={() => setActiveTab('fees')}
              className={`py-4 px-2 border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'fees'
                  ? 'border-warm-coral text-warm-coral font-semibold'
                  : 'border-transparent text-warm-gray hover:text-deep-forest'
              }`}
            >
              Adoption Fees
            </button>
            <button
              onClick={() => setActiveTab('programs')}
              className={`py-4 px-2 border-b-4 transition-colors whitespace-nowrap ${
                activeTab === 'programs'
                  ? 'border-warm-coral text-warm-coral font-semibold'
                  : 'border-transparent text-warm-gray hover:text-deep-forest'
              }`}
            >
              Special Programs
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {activeTab === 'available' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-2xl p-6 mb-8 shadow-md">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-sky-blue/10 p-2 rounded-lg">
                  <Info className="text-sky-blue" size={24} />
                </div>
                <h2 className="font-semibold text-xl text-deep-forest">How to Adopt</h2>
              </div>
              <p className="text-warm-gray mb-4">
                To learn more about any of our available pets shown below, call our adoption staff at 
                <span className="font-semibold text-warm-coral"> 218-722-5341</span> starting at 10:30am. 
                If you have an interest in one specific animal please call early and often as our adoptions 
                are done on first come-first serve basis.
              </p>
              <div className="bg-cream-white rounded-lg p-4">
                <p className="text-sm text-warm-gray">
                  <span className="font-semibold">Adoption Hours:</span> Tuesday-Friday 12-6pm, Saturday-Sunday 12-4pm (Closed Mondays)
                </p>
              </div>
            </div>

            {/* Pet Gallery iframe */}
            <div className="bg-white rounded-2xl p-6 shadow-md">
              <h2 className="font-display text-3xl text-deep-forest mb-6 text-center">Available Pets</h2>
              <div className="w-full">
                <iframe 
                  id="adopets-iframe" 
                  src="https://iframe.adopets.com/animal-allies?shelter_uuid=840763a5-5583-4912-aa12-7f21a7a3c8d9&g=15&p=15" 
                  width="100%" 
                  frameBorder="0" 
                  height="1436"
                  className="w-full"
                  title="Available pets for adoption"
                />
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'process' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-2 gap-8"
          >
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h2 className="font-display text-3xl text-deep-forest mb-6">Adoption Process</h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-warm-coral text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-deep-forest mb-2">Find Your Match</h3>
                    <p className="text-warm-gray">
                      Browse our available pets online or visit us during adoption hours to meet them in person.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-warm-coral text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-deep-forest mb-2">Call to Reserve</h3>
                    <p className="text-warm-gray">
                      Call us at 218-722-5341 starting at 10:30am to express interest in a specific pet.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-warm-coral text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-deep-forest mb-2">Meet & Greet</h3>
                    <p className="text-warm-gray">
                      Visit during our adoption hours to meet your potential new family member in our Get Acquainted rooms.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-warm-coral text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-deep-forest mb-2">Complete Adoption</h3>
                    <p className="text-warm-gray">
                      Once you've decided, the adoption process takes about 30 minutes to complete. Take your new friend home!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-sky-blue/10 rounded-2xl p-8">
                <h3 className="font-display text-2xl text-deep-forest mb-4">Fear Free Shelter</h3>
                <p className="text-warm-gray mb-4">
                  In 2020 our entire shelter went through Fear Free Shelter training. This focuses on reducing 
                  stress and improving the animals' experience in the shelter.
                </p>
                <p className="text-warm-gray">
                  We have seen amazing results including less behavioral issues, quieter and more calm dogs, 
                  and less bite incidents. This results in dogs finding homes faster and having a much less 
                  stressful time while at the shelter.
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="text-warm-coral" size={24} />
                  <h3 className="font-semibold text-xl text-deep-forest">Adoption Hours</h3>
                </div>
                <div className="space-y-2">
                  <p className="text-warm-gray">
                    <span className="font-semibold">Tuesday - Friday:</span> 12:00 PM - 6:00 PM
                  </p>
                  <p className="text-warm-gray">
                    <span className="font-semibold">Saturday - Sunday:</span> 12:00 PM - 4:00 PM
                  </p>
                  <p className="text-warm-gray">
                    <span className="font-semibold">Monday:</span> CLOSED
                  </p>
                </div>
                <div className="mt-4 p-4 bg-cream-white rounded-lg">
                  <p className="text-sm text-warm-gray">
                    Phone lines open at 10:30 AM for adoption inquiries
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'fees' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-2xl p-8 shadow-md mb-8">
              <h2 className="font-display text-3xl text-deep-forest mb-6">Adoption Fees</h2>
              <p className="text-warm-gray mb-6">
                Our adoption fees are based on a number of factors such as age, size, breed, or medical needs. 
                A higher adoption fee does not mean that we value that particular animal any more than another; 
                it is simply a matter of supply and demand.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-warm-coral/10 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-warm-coral text-white p-2 rounded-lg">
                      <Heart size={24} fill="currentColor" />
                    </div>
                    <h3 className="font-semibold text-xl text-deep-forest">Cats & Kittens</h3>
                  </div>
                  <p className="font-display text-3xl text-warm-coral mb-2">$25 - $225</p>
                  <p className="text-warm-gray">Variable pricing based on age and demand</p>
                </div>

                <div className="bg-sky-blue/10 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-sky-blue text-white p-2 rounded-lg">
                      <Heart size={24} fill="currentColor" />
                    </div>
                    <h3 className="font-semibold text-xl text-deep-forest">Dogs & Puppies</h3>
                  </div>
                  <p className="font-display text-3xl text-sky-blue mb-2">$75 - $525</p>
                  <p className="text-warm-gray">Variable pricing based on age, size, and breed</p>
                </div>
              </div>

              <div className="bg-sage-green/10 rounded-xl p-6">
                <h3 className="font-semibold text-xl text-deep-forest mb-4">What's Included in Your Adoption Fee</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Microchip and registration fee</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Veterinarian health exam</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Age-appropriate vaccinations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Spaying/neutering</span>
                    </li>
                  </ul>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Fecal exam</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Flea/tick treatments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Heartworm testing (dogs)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-sage-green mt-1">✓</span>
                      <span className="text-warm-gray">Post-adoption support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'programs' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            <div className="bg-white rounded-2xl p-8 shadow-md">
              <div className="flex items-center gap-3 mb-6">
                <Award className="text-golden-yellow" size={32} />
                <h2 className="font-display text-3xl text-deep-forest">Special Adoption Programs</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-soft-lavender/10 rounded-xl p-6">
                  <h3 className="font-semibold text-xl text-deep-forest mb-3">Sidekicks for Seniors</h3>
                  <div className="bg-soft-lavender text-white px-4 py-2 rounded-full inline-block mb-4">
                    <span className="font-bold">$50 OFF</span> for adopters 60+
                  </div>
                  <p className="text-warm-gray mb-4">
                    Animal Allies receives grant funding from Pets for the Elderly, a nationwide program 
                    that encourages seniors to adopt companion animals.
                  </p>
                  <p className="text-warm-gray">
                    Benefits include lowering the risk of heart disease, lowering blood pressure, 
                    reducing stress, and providing companionship.
                  </p>
                </div>

                <div className="bg-sky-blue/10 rounded-xl p-6">
                  <h3 className="font-semibold text-xl text-deep-forest mb-3">Buddy Program</h3>
                  <div className="space-y-2 mb-4">
                    <div className="bg-sky-blue text-white px-4 py-2 rounded-full inline-block">
                      <span className="font-bold">$25 OFF</span> two cats
                    </div>
                    <div className="bg-sky-blue text-white px-4 py-2 rounded-full inline-block">
                      <span className="font-bold">$50 OFF</span> two dogs or dog + cat
                    </div>
                  </div>
                  <p className="text-warm-gray">
                    Adopt two pets at once and receive a discount on your total adoption fees. 
                    Double the love, double the fun!
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-md">
              <h3 className="font-display text-2xl text-deep-forest mb-6">Barn Cat/Working Cat Program</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-warm-coral/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <span className="font-display text-2xl text-warm-coral">$25</span>
                  </div>
                  <p className="text-warm-gray">Per barn cat</p>
                </div>
                <div className="text-center">
                  <div className="bg-sky-blue/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <span className="font-display text-2xl text-sky-blue">2 for 1</span>
                  </div>
                  <p className="text-warm-gray">Buddy discount available</p>
                </div>
                <div className="text-center">
                  <div className="bg-sage-green/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                    <Heart className="text-sage-green" size={32} fill="currentColor" />
                  </div>
                  <p className="text-warm-gray">All cats spayed/neutered & vaccinated</p>
                </div>
              </div>
              <p className="text-warm-gray mt-6 text-center max-w-3xl mx-auto">
                We believe that all cats deserve to live out their best life, even if that means not being a housecat. 
                Perfect for barns, sheds, businesses, or other outdoor housing outside Duluth city limits.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Adopt;